import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Button, Spinner } from '@treams/treams-ui';

function LoadConnectorConfigField(props: {
	field: { key: string };
	connectorType: graphql.ConnectorType;
	placeholder: ReactNode;
	onLoad: (field: graphql.ConnectorConfigField) => void;
}) {
	const query = graphql.useConnectorConfigFieldQuery({
		variables: {
			connectorType: props.connectorType,
			key: props.field.key,
		},
		fetchPolicy: 'no-cache',
	});

	return (
		<>
			{query.loading && (
				<Flex alignItems="center">
					{props.placeholder}
					<Spinner ml={2} />
				</Flex>
			)}
			{query.data && props.onLoad(query.data.connectorConfigField as graphql.ConnectorConfigField)}
		</>
	);
}

export default function ConfigurationFieldEdit<T extends graphql.ConnectorConfigField>(
	props: PropsWithChildren<{
		field: T;
		autoFocus?: boolean;
		connectorType: graphql.ConnectorType;
		change?: Partial<graphql.UpdateConnectorConfigFieldInput>;
		onChange: (input: Partial<graphql.UpdateConnectorConfigFieldInput>) => void;
		editingChildren: (field: graphql.ConnectorConfigField) => ReactNode;
	}>,
) {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(false);
	const [version, setVersion] = useState(1);
	const [previousChange, setPreviousChange] = useState(props.change);

	useEffect(() => {
		if (previousChange !== undefined && props.change === undefined) {
			setEditing(false);
			setVersion(version + 1);
		}

		setPreviousChange(props.change);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.change]);

	return (
		<>
			{editing && (
				<LoadConnectorConfigField
					field={props.field}
					connectorType={props.connectorType}
					onLoad={(field) => props.editingChildren(field)}
					placeholder={props.children}
				/>
			)}
			{!editing && (
				<Flex alignItems="center">
					{props.children}
					<Button
						type="secondary"
						title={t('connectors.settings.configuration.changeValue')}
						onClick={() => setEditing(true)}
						ml={props.children ? 2 : 0}
					/>
				</Flex>
			)}
		</>
	);
}
